/**
 * Resource activity panel (prototype, GDN-2579): the resource's event
 * history from the ClickHouse analytics store — change history (creates /
 * updates / deletes / cross-events) and access history (view/list exhaust).
 * Rows expand to show the event's full payload and correlation id.
 *
 * Renders below the resource detail card. Degrades to nothing when the
 * backend has no analytics store configured — the panel is additive,
 * never load-bearing.
 */
import { Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { ResourceActivityEntry } from '../../../store/generated/admin';
import { useGetResourceActivityQuery } from '../store/hooks';
import { CardTitle } from './card-title';

type ActivityKind = 'all' | 'changes' | 'access';

const formatDateTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const ActivityRow = ({
  entry,
  resourceType,
  resourceId,
  organizationId,
}: {
  entry: ResourceActivityEntry;
  resourceType: string;
  resourceId: string;
  organizationId: string;
}) => {
  const [open, setOpen] = useState(false);
  // Payloads are fetched lazily per event: the list query omits them (the
  // JSON column costs seconds across a busy resource's range; one event is
  // ~400ms).
  const { data: detail, isFetching: detailLoading } = useGetResourceActivityQuery(
    {
      resourceType,
      resourceId,
      organizationId,
      eventId: entry.event_id,
      limit: 1,
    },
    { skip: !open },
  );
  const payload = detail?.entries?.[0]?.data;

  return (
    <Fragment>
      <TableRow hover onClick={() => setOpen(previous => !previous)} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.occurred_at)}</TableCell>
        <TableCell>
          <Chip
            size="xSmall"
            variant="soft"
            color={entry.is_access ? 'default' : 'info'}
            label={entry.event_type}
          />
        </TableCell>
        <TableCell>{entry.action ?? '—'}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {entry.actor_kind === 'system' ? '⚙ ' : ''}
          {entry.actor_id ?? entry.user_id ?? '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: 'monospace' }}
              >
                event_id: {entry.event_id}
              </Typography>
              {entry.correlation_id && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontFamily: 'monospace' }}
                >
                  correlation_id: {entry.correlation_id}
                </Typography>
              )}
              <Box sx={{ mb: 1 }} />
              {detailLoading ? (
                <CircularProgress size={18} />
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: 12,
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(payload ?? {}, null, 2)}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

interface ResourceActivityCardProps {
  resourceType: string;
  resourceId: string;
  organizationId: string;
}

export const ResourceActivityCard = ({
  resourceType,
  resourceId,
  organizationId,
}: ResourceActivityCardProps) => {
  const [kind, setKind] = useState<ActivityKind>('all');
  const { data, isLoading, error } = useGetResourceActivityQuery({
    resourceType,
    resourceId,
    organizationId,
    kind,
    limit: 100,
  });

  // Only a backend WITHOUT an analytics store hides the panel; any other
  // failure (store down, query error) must stay visible as a warning —
  // conflating them made the card vanish on transient errors.
  const errorCode =
    error && 'data' in error
      ? (error.data as { error_code?: string } | undefined)?.error_code
      : undefined;
  if (errorCode === 'activity_store_not_configured') return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        disableTypography
        title={<CardTitle>Activity</CardTitle>}
        subheader={
          <Typography variant="body2" color="text.secondary">
            Event history from the analytics store
          </Typography>
        }
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, next: ActivityKind | null) => {
              if (next) setKind(next);
            }}
            aria-label="Activity kind"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="changes">Changes</ToggleButton>
            <ToggleButton value="access">Access</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <CardContent>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && error && (
          <Alert severity="warning">
            The analytics store did not answer; activity is temporarily unavailable.
          </Alert>
        )}
        {!isLoading && !error && !data?.entries?.length && (
          <Typography color="text.secondary">No recorded activity for this resource.</Typography>
        )}
        {!isLoading && !error && !!data?.entries?.length && (
          <Table size="small" aria-label="Resource activity">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Actor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.entries.map(entry => (
                <ActivityRow
                  key={entry.event_id}
                  entry={entry}
                  resourceType={resourceType}
                  resourceId={resourceId}
                  organizationId={organizationId}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
